import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { TransportApi, DriverApi } from "@/api";

export const useTransportsStore = defineStore("transports", () => {
  // ----- state -----
  // Транспортные средства для страниц с пагинацией (autopark/transports и AddTransportModal)
  const transports = ref([]);
  // Транспортные средства для боковой панели (aside)
  const asideTransports = ref([]);
  // Текущая страница для боковой панели
  const asideCurrentPage = ref(1);
  // Флаг наличия еще данных для боковой панели
  const asideHasMore = ref(true);
  const currentTransport = ref(null);
  // Загрузка для страниц с пагинацией
  const loading = ref(false);
  // Загрузка карточки конкретного ТС (открытие страницы open.vue) —
  // отдельный флаг, чтобы не мигал скелет списка при загрузке карточки.
  const loadingTransport = ref(false);
  // Загрузка для боковой панели
  const loadingAside = ref(false);
  // Загрузка для операций сохранения (создание/редактирование)
  const saving = ref(false);
  // Пагинация для страниц (autopark/transports и AddTransportModal)
  const pagination = ref({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 10,
    hasMore: true,
  });
  const error = ref(null);
  // Фильтры
  const filters = ref({
    search: "",
    condition: null,
    status: null,
    driver_id: null,
    type: null,
  });
  // Опции для фильтров
  const filterOptions = ref({
    conditions: [],
    statuses: [],
    drivers: [],
    types: [],
  });
  // Состояние загрузки опций фильтров
  const loadingFilterOptions = ref(false);
  // Транспортные операции
  const transportOperations = ref([]);
  // Пагинация для транспортных операций
  const operationsPagination = ref({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 10,
    hasMore: true,
  });
  // Загрузка транспортных операций
  const loadingOperations = ref(false);
  // Фильтры для транспортных операций
  const operationsFilters = ref({
    type: null,
  });

  // ----- getters -----
  /**
   * Получение списка транспортных средств для страниц с пагинацией
   * @returns {Array}
   */
  const getTransports = computed(() => transports.value);

  /**
   * Проверка наличия еще транспортных средств для загрузки на страницах с пагинацией
   * @returns {Boolean}
   */
  const hasMoreTransports = computed(() => pagination.value.hasMore);

  /**
   * Получение информации о пагинации
   * @returns {Object}
   */
  const getPagination = computed(() => pagination.value);

  /**
   * Проверка состояния загрузки для страниц с пагинацией
   * @returns {Boolean}
   */
  const isLoading = computed(() => loading.value);

  /**
   * Получение текущих фильтров
   * @returns {Object}
   */
  const getFilters = computed(() => filters.value);

  /**
   * Получение опций для фильтров
   * @returns {Object}
   */
  const getFilterOptions = computed(() => filterOptions.value);

  /**
   * Получение списка транспортных операций
   * @returns {Array}
   */
  const getTransportOperations = computed(() => transportOperations.value);

  /**
   * Получение информации о пагинации для транспортных операций
   * @returns {Object}
   */
  const getOperationsPagination = computed(() => operationsPagination.value);

  /**
   * Проверка загрузки данных транспортных операций
   * @returns {Boolean}
   */
  const isLoadingOperations = computed(() => loadingOperations.value);

  // ----- actions -----
  /**
   * Получение списка транспортных средств с пагинацией для боковой панели (aside)
   * @param {Boolean} loadMore - Загрузить следующую порцию данных или сбросить и загрузить сначала
   * @param {Object} params - Дополнительные параметры запроса
   * @returns {Promise<Array>}
   */
  async function fetchAsideTransports(loadMore = false, params = {}) {
    if (loadingAside.value) return asideTransports.value;

    loadingAside.value = true;

    try {
      const page = loadMore ? (asideCurrentPage.value || 1) + 1 : 1;

      const { data, meta } = await TransportApi.fetchTransports({
        ...params,
        page,
        limit: 8,
      });

      if (data) {
        // Сохраняем оригинальные данные без форматирования
        const formattedTransports = data;

        // Обновляем список транспортных средств
        if (loadMore) {
          asideTransports.value = [...asideTransports.value, ...formattedTransports];
        } else {
          asideTransports.value = formattedTransports;
        }

        // Сохраняем текущую страницу для бесконечной загрузки
        asideCurrentPage.value = page;
        asideHasMore.value = meta ? page < meta.last_page : formattedTransports.length > 0;

        return asideTransports.value;
      }

      return [];
    } catch (error) {
      console.error("Ошибка при получении списка транспортных средств для боковой панели:", error);
      setError(error);
      return [];
    } finally {
      loadingAside.value = false;
    }
  }

  /**
   * Получение списка транспортных средств с пагинацией для страниц (autopark/transports и AddTransportModal)
   * @param {Boolean} loadMore - Загрузить следующую страницу или сбросить и загрузить первую
   * @param {Object} params - Дополнительные параметры запроса
   * @returns {Promise<Array>}
   */
  async function fetchTransports(loadMore = false, params = {}) {
    if (loading.value) return transports.value;

    loading.value = true;

    try {
      // Вычисляем страницу:
      //  - loadMore=true → следующая страница (бесконечная подгрузка);
      //  - params.page задан явно → используем его (пагинация);
      //  - иначе (применение фильтра / первичная загрузка) → всегда сбрасываем на стр.1,
      //    чтобы новый фильтр не показывал текущую (возможно несуществующую при меньшем наборе) страницу.
      let page;

      if (loadMore) {
        page = pagination.value.currentPage + 1;
      } else if (params.page) {
        page = params.page;
      } else {
        page = 1;
      }

      // Добавляем фильтры к параметрам запроса
      const filtersData = { ...filters.value };

      // Удаляем page из params, чтобы избежать дублирования
      const { ...restParams } = params;

      const { data, meta } = await TransportApi.fetchTransports({
        ...restParams,
        ...filtersData,
        page,
        limit: pagination.value.perPage,
      });

      if (data) {
        // Обновляем список транспортных средств
        if (loadMore) {
          transports.value = [...transports.value, ...data];
        } else {
          transports.value = data;
        }

        // Обновляем информацию о пагинации
        if (meta) {
          pagination.value = {
            currentPage: meta.current_page || page,
            lastPage: meta.last_page || page,
            total: meta.total || 0,
            perPage: meta.per_page || 10,
            hasMore: page < (meta.last_page || 1),
          };
        } else {
          // Если мета-информация отсутствует, предполагаем, что больше страниц нет
          pagination.value.currentPage = page;
          pagination.value.hasMore = data.length > 0;
        }

        return transports.value;
      }

      return [];
    } catch (error) {
      console.error("Ошибка при получении списка транспортных средств:", error);
      setError(error);
      return [];
    } finally {
      loading.value = false;
    }
  }

  /**
   * Загрузка дополнительных транспортных средств
   * @param {Object} params - Дополнительные параметры запроса
   * @returns {Promise<Array>}
   */
  async function loadMoreTransports(params = {}) {
    if (!pagination.value.hasMore || loading.value) {
      return transports.value;
    }

    return fetchTransports(true, params);
  }

  /**
   * Получение информации о конкретном транспортном средстве
   * @param {Number|String} id - Идентификатор транспортного средства
   * @returns {Promise<Object|null>}
   */
  async function fetchTransportById(id) {
    loadingTransport.value = true;

    try {
      const response = await TransportApi.fetchTransportById(id);

      if (response && response.data) {
        currentTransport.value = response.data;
        return currentTransport.value;
      }

      return null;
    } catch (error) {
      console.error(`Ошибка при получении информации о транспортном средстве с ID ${id}:`, error);
      setError(error);
      return null;
    } finally {
      loadingTransport.value = false;
    }
  }

  /**
   * Сброс текущего транспортного средства
   */
  function resetCurrentTransport() {
    currentTransport.value = null;
  }

  /**
   * Установка значения фильтра
   * @param {String} filterName - Имя фильтра
   * @param {*} value - Значение фильтра
   */
  function setFilter(filterName, value) {
    if (!(filterName in filters.value)) return;

    filters.value[filterName] = value;

    // Сбрасываем пагинацию при изменении фильтра
    pagination.value.currentPage = 1;
    // Загружаем данные с новыми фильтрами
    fetchTransports(false);
  }

  /**
   * Сброс всех фильтров
   */
  function resetFilters() {
    filters.value = {
      search: "",
      condition: null,
      status: null,
      driver_id: null,
      type: null,
    };
    // Загружаем данные без фильтров
    fetchTransports(false);
  }

  /**
   * Загрузка опций для фильтров
   * @returns {Promise<Object>}
   */
  async function fetchFilterOptions() {
    if (loadingFilterOptions.value) return filterOptions.value;

    loadingFilterOptions.value = true;

    try {
      // Загружаем все опции параллельно
      const [conditionsResponse, statusesResponse, driversResponse, typesResponse] = await Promise.all([
        TransportApi.fetchTransportConditions({ limit: 100 }),
        TransportApi.fetchTransportStatuses({ limit: 100 }),
        DriverApi.fetchDrivers({ limit: 100 }),
        TransportApi.fetchTransportTypes({ limit: 100 }),
      ]);

      // Обновляем опции фильтров
      if (conditionsResponse && conditionsResponse.data) {
        filterOptions.value.conditions = conditionsResponse.data;
      }

      if (statusesResponse && statusesResponse.data) {
        filterOptions.value.statuses = statusesResponse.data;
      }

      if (driversResponse && driversResponse.data) {
        filterOptions.value.drivers = driversResponse.data;
      }

      if (typesResponse && typesResponse.data) {
        filterOptions.value.types = typesResponse.data;
      }

      return filterOptions.value;
    } catch (error) {
      console.error("Ошибка при загрузке опций фильтров:", error);
      setError(error);
      return filterOptions.value;
    } finally {
      loadingFilterOptions.value = false;
    }
  }

  /**
   * Получение списка транспортных операций с пагинацией
   * @param {Number|String} transportId - Идентификатор транспортного средства
   * @param {Boolean} loadMore - Загрузить следующую страницу или сбросить и загрузить первую
   * @param {Object} params - Дополнительные параметры запроса
   * @returns {Promise<Array>}
   */
  async function fetchTransportOperations(transportId, loadMore = false, params = {}) {
    if (loadingOperations.value) {
      return transportOperations.value;
    }

    loadingOperations.value = true;

    try {
      // Если в параметрах уже есть page, используем его, иначе вычисляем
      const pageNum = params.page
        ? parseInt(params.page)
        : loadMore
        ? operationsPagination.value.currentPage + 1
        : 1;

      // Сразу обновляем текущую страницу в сторе
      operationsPagination.value.currentPage = pageNum;

      // Удаляем page из параметров, чтобы избежать дублирования
      const { ...restParams } = params;

      // Добавляем фильтры к параметрам запроса
      const filters = {};
      if (operationsFilters.value.type) filters.type = operationsFilters.value.type;

      const response = await TransportApi.fetchTransportOperations(transportId, {
        ...restParams,
        ...filters,
        page: pageNum,
      });

      if (response && response.data) {
        // Обновляем список транспортных операций
        if (loadMore) {
          transportOperations.value = [...transportOperations.value, ...response.data];
        } else {
          transportOperations.value = response.data;
        }

        // Обновляем информацию о пагинации
        if (response.meta) {
          const newPagination = {
            currentPage: response.meta.current_page || pageNum,
            lastPage: response.meta.last_page || 1,
            total: response.meta.total || 0,
            perPage: response.meta.per_page || 10,
            hasMore: (response.meta.current_page || 1) < (response.meta.last_page || 1),
          };

          operationsPagination.value = newPagination;
        }

        return transportOperations.value;
      }

      return [];
    } catch (error) {
      console.error("Ошибка при получении списка транспортных операций:", error);
      setError(error);
      return [];
    } finally {
      loadingOperations.value = false;
    }
  }

  /**
   * Загрузка дополнительных транспортных операций
   * @param {Number|String} transportId - Идентификатор транспортного средства
   * @param {Object} params - Дополнительные параметры запроса
   * @returns {Promise<Array>}
   */
  async function loadMoreTransportOperations(transportId, params = {}) {
    if (!operationsPagination.value.hasMore || loadingOperations.value) {
      return transportOperations.value;
    }

    return fetchTransportOperations(transportId, true, params);
  }

  /**
   * Установка фильтра для транспортных операций
   * @param {String} filterName - Имя фильтра
   * @param {*} value - Значение фильтра
   * @param {Number|String} transportId - Идентификатор транспортного средства
   */
  function setOperationsFilter(filterName, value, transportId) {
    if (filterName in operationsFilters.value) {
      operationsFilters.value[filterName] = value;

      // Сбрасываем пагинацию при изменении фильтра
      operationsPagination.value.currentPage = 1;

      // Загружаем данные с новыми фильтрами
      if (transportId) {
        fetchTransportOperations(transportId, false);
      }
    }
  }

  /**
   * Сброс всех фильтров для транспортных операций
   * @param {Number|String} transportId - Идентификатор транспортного средства
   */
  function resetOperationsFilters(transportId) {
    operationsFilters.value = {
      type: null,
    };

    // Загружаем данные без фильтров
    if (transportId) {
      fetchTransportOperations(transportId, false);
    }
  }

  /**
   * Создание нового транспортного средства
   * @param {Object} transportData - Данные транспортного средства
   * @returns {Promise<Object>}
   */
  async function createTransport(transportData) {
    try {
      saving.value = true;

      const response = await TransportApi.createTransport(transportData);

      if (response && response.data) {
        // Форматируем данные для сохранения единой структуры объектов
        const formattedData = response.data;

        // Создаем новый объект, чтобы гарантировать реактивность
        const newTransport = { ...formattedData };

        // Устанавливаем новое ТС как текущее
        currentTransport.value = newTransport;

        // Вместо ручного unshift + total++ (давало 11 карточек при perPage 10 и ломало
        // пагинацию) перезагружаем список с первой страницы — корректный размер и meta.
        pagination.value.currentPage = 1;
        await fetchTransports(false, { page: 1 });

        return newTransport;
      }

      return null;
    } catch (error) {
      console.error("Ошибка при создании транспортного средства:", error);
      setError(error);
      throw error;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Редактирование транспортного средства
   * @param {Number|String} id - Идентификатор транспортного средства
   * @param {Object} transportData - Данные транспортного средства
   * @returns {Promise<Object>}
   */
  async function updateTransport(id, transportData) {
    try {
      saving.value = true;

      // id из route приходит строкой — приводим к числу, иначе findIndex (t.id === id)
      // не найдёт элемент с number-id и создаст дубликат в списке.
      id = Number(id);

      const response = await TransportApi.updateTransport(id, transportData);

      if (response && response.data) {
        // Форматируем данные для сохранения единой структуры объектов
        const formattedData = response.data;

        // Обновляем транспортное средство в списке
        const index = transports.value.findIndex((t) => t.id === id);

        if (index !== -1) {
          // Создаем новый объект для обновления, чтобы гарантировать реактивность
          const updatedTransport = { ...formattedData };
          // Обновляем элемент в массиве
          transports.value.splice(index, 1, updatedTransport);
        } else {
          // Если ТС не найдено в списке, добавляем его
          const newTransport = { ...formattedData };
          transports.value.push(newTransport);
        }

        // Если это текущее транспортное средство, обновляем его
        if (currentTransport.value && currentTransport.value.id === id) {
          currentTransport.value = { ...formattedData };
        }

        return formattedData;
      }

      return null;
    } catch (error) {
      console.error("Ошибка при редактировании транспортного средства:", error);
      setError(error);
      throw error;
    } finally {
      saving.value = false;
    }
  }

  // Внутренний хелпер для установки ошибки (имя error используется как параметр в catch-блоках)
  function setError(value) {
    error.value = value;
  }

  return {
    // state
    transports,
    asideTransports,
    asideCurrentPage,
    asideHasMore,
    currentTransport,
    loading,
    loadingTransport,
    loadingAside,
    saving,
    pagination,
    error,
    filters,
    filterOptions,
    loadingFilterOptions,
    transportOperations,
    operationsPagination,
    loadingOperations,
    operationsFilters,
    // getters
    getTransports,
    hasMoreTransports,
    getPagination,
    isLoading,
    getFilters,
    getFilterOptions,
    getTransportOperations,
    getOperationsPagination,
    isLoadingOperations,
    // actions
    fetchAsideTransports,
    fetchTransports,
    loadMoreTransports,
    fetchTransportById,
    resetCurrentTransport,
    setFilter,
    resetFilters,
    fetchFilterOptions,
    fetchTransportOperations,
    loadMoreTransportOperations,
    setOperationsFilter,
    resetOperationsFilters,
    createTransport,
    updateTransport,
  };
});
